import React, { useState } from 'react';
import { MoreVertical, Maximize2, EyeOff, Star, Download, ChevronDown, ChevronUp, Send } from 'lucide-react';
import GradientChip from './GradientChip';
import GradientIconCircle from './GradientIconCircle';
export interface WidgetQuestion {
  id: string;
  text: string;
}
export interface WidgetQA {
  question: string;
  answer: string;
  timestamp: string;
}
interface BentoWidgetCardProps {
  id: string;
  title: string;
  size: 'S' | 'M' | 'L';
  lastUpdated: string;
  children: React.ReactNode;
  questions?: WidgetQuestion[];
  qaHistory?: WidgetQA[];
  onExpand?: () => void;
  onHide?: () => void;
  onPin?: () => void;
  onFavorite?: () => void;
  onExport?: () => void;
  onAsk?: (question: string) => void;
  isPinned?: boolean;
  isFavorite?: boolean;
}
const BentoWidgetCard: React.FC<BentoWidgetCardProps> = ({
  id,
  title,
  size,
  lastUpdated,
  children,
  questions = [],
  qaHistory = [],
  onExpand,
  onHide,
  onPin,
  onFavorite,
  onExport,
  onAsk,
  isPinned = false,
  isFavorite = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [qaExpanded, setQaExpanded] = useState(false);
  const [customQuestion, setCustomQuestion] = useState('');
  const sizeClasses = {
    S: 'col-span-12 md:col-span-6 lg:col-span-3',
    M: 'col-span-12 md:col-span-6 lg:col-span-5',
    L: 'col-span-12 lg:col-span-7'
  };
  const handleAskCustom = () => {
    if (customQuestion.trim() && onAsk) {
      onAsk(customQuestion);
      setCustomQuestion('');
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskCustom();
    }
  };
  return <div className={`${sizeClasses[size]} bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden`} data-id="element-1207">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-1208">
        <div className="flex-1" data-id="element-1209">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-1210">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-1211">
            Aggiornato {lastUpdated}
          </p>
        </div>
        <div className="relative" data-id="element-1212">
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1213">
            <MoreVertical className="h-4 w-4 text-gray-500" data-id="element-1214" />
          </button>

          {menuOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} data-id="element-1215" />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20" data-id="element-1216">
                {onExpand && <button onClick={() => {
              onExpand();
              setMenuOpen(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center" data-id="element-1217">
                    <Maximize2 className="h-4 w-4 mr-2 text-gray-500" data-id="element-1218" />
                    Espandi
                  </button>}
                {onHide && <button onClick={() => {
              onHide();
              setMenuOpen(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center" data-id="element-1219">
                    <EyeOff className="h-4 w-4 mr-2 text-gray-500" data-id="element-1220" />
                    Nascondi
                  </button>}
                {onPin && <button onClick={() => {
              onPin();
              setMenuOpen(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center" data-id="element-1221">
                    <Star className={`h-4 w-4 mr-2 ${isPinned ? 'text-yellow-500 fill-yellow-500' : 'text-gray-500'}`} data-id="element-1222" />
                    {isPinned ? 'Rimuovi fissa' : 'Fissa'}
                  </button>}
                {onFavorite && <button onClick={() => {
              onFavorite();
              setMenuOpen(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center" data-id="element-1223">
                    <Star className={`h-4 w-4 mr-2 ${isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-500'}`} data-id="element-1224" />
                    {isFavorite ? 'Rimuovi preferiti' : 'Aggiungi a preferiti'}
                  </button>}
                {onExport && <button onClick={() => {
              onExport();
              setMenuOpen(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center border-t border-gray-100" data-id="element-1225">
                    <Download className="h-4 w-4 mr-2 text-gray-500" data-id="element-1226" />
                    Esporta snapshot
                  </button>}
              </div>
            </>}
        </div>
      </div>

      {/* Body */}
      <div className="p-4" data-id="element-1227">{children}</div>

      {/* Footer - Questions */}
      {(questions.length > 0 || onAsk) && <div className="px-4 py-3 border-t border-gray-100 bg-gray-50" data-id="element-1228">
          <p className="text-xs font-medium text-gray-700 mb-2" data-id="element-1229">Domande</p>

          {/* Predefined AI Chips */}
          {questions.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-1230">
              {questions.map(q => <GradientChip key={q.id} size="xs" onClick={() => onAsk && onAsk(q.text)} data-id="element-1231">
                  {q.text}
                </GradientChip>)}
            </div>}

          {/* Custom Question Input */}
          {onAsk && <div className="flex items-center gap-2" data-id="element-1232">
              <input type="text" value={customQuestion} onChange={e => setCustomQuestion(e.target.value)} onKeyPress={handleKeyPress} placeholder="Chiedi a GB Intelligence…" className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1233" />
              <GradientIconCircle size={28} onClick={handleAskCustom} data-id="element-1234">
                <Send className="h-3.5 w-3.5 text-purple-600" data-id="element-1235" />
              </GradientIconCircle>
            </div>}

          {/* Q&A History Toggle */}
          {qaHistory.length > 0 && <button onClick={() => setQaExpanded(!qaExpanded)} className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 mt-3 transition-colors" data-id="element-1236">
              {qaExpanded ? <ChevronUp className="h-3 w-3" data-id="element-1237" /> : <ChevronDown className="h-3 w-3" data-id="element-1238" />}
              {qaExpanded ? 'Nascondi' : 'Mostra'} conversazione (
              {qaHistory.length})
            </button>}

          {/* Q&A History */}
          {qaExpanded && qaHistory.length > 0 && <div className="mt-3 space-y-2 max-h-48 overflow-y-auto" data-id="element-1239">
              {qaHistory.map((qa, index) => <div key={index} className="text-xs" data-id="element-1240">
                  <p className="font-medium text-gray-900 mb-1" data-id="element-1241">
                    Q: {qa.question}
                  </p>
                  <div className="bg-white rounded p-2 border border-purple-100" data-id="element-1242">
                    <p className="text-gray-700" data-id="element-1243">{qa.answer}</p>
                    <p className="text-gray-400 text-xs mt-1" data-id="element-1244">{qa.timestamp}</p>
                  </div>
                </div>)}
            </div>}
        </div>}
    </div>;
};
export default BentoWidgetCard;